import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import IconButton from "@mui/material/IconButton";
import type { SxProps, Theme } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { MouseEvent } from "react";
import { useTranslation } from "react-i18next";

import { track } from "~/lib/analytics";
import { fetchSavedSlugs, saveArticle, unsaveArticle } from "~/lib/api";
import { queryKeys } from "~/lib/query-client";
import { AnalyticsEventName } from "~/types/analytics";
import type { SavedSlugs } from "~/types/saved";

/**
 * The heart. Lit while the article is saved for later; a tap toggles it.
 *
 * Every heart reads one shared query — the member's saved slugs — so a feed of thirty
 * cards costs one request, and the toggle rewrites that cache optimistically so the
 * heart answers the finger before the server does. A failure puts it back and the
 * next read settles it.
 *
 * Inside a card the button sits beside the link, not in it, and swallows its own tap:
 * a heart that opened the article would be worse than no heart.
 */
export function SaveButton({
  slug,
  userId,
  articleId,
  sx,
  testId = "news.save",
}: {
  slug: string;
  userId: string;
  articleId?: string;
  sx?: SxProps<Theme>;
  testId?: string;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const key = queryKeys.savedSlugs(userId);

  const { data } = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => fetchSavedSlugs(signal),
  });
  const saved = data?.slugs.includes(slug) ?? false;

  const toggle = useMutation<
    void,
    Error,
    boolean,
    { previous: SavedSlugs | undefined }
  >({
    mutationFn: async (next) => {
      if (next) await saveArticle(slug);
      else await unsaveArticle(slug);
    },
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<SavedSlugs>(key);
      queryClient.setQueryData<SavedSlugs>(key, (current) => {
        const slugs = (current?.slugs ?? []).filter((item) => item !== slug);
        return { slugs: next ? [slug, ...slugs] : slugs };
      });
      return { previous };
    },
    onError: (_error, _next, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSuccess: (_result, next) => {
      if (next)
        track(AnalyticsEventName.ContentSave, {
          subjectType: "news",
          subjectId: articleId ?? null,
          subjectKey: slug,
        });
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.saved(userId) }),
  });

  const label = t(saved ? "saved.unsave" : "saved.save");

  return (
    <IconButton
      aria-label={label}
      aria-pressed={saved}
      title={label}
      disabled={toggle.isPending}
      onClick={(clickEvent: MouseEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
        toggle.mutate(!saved);
      }}
      data-testid={testId}
      data-saved={saved ? "true" : "false"}
      sx={[
        {
          width: 40,
          height: 40,
          color: saved ? "error.main" : "text.secondary",
          backgroundColor: "background.paper",
          boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
          "&:hover": { backgroundColor: "background.paper" },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {saved ? (
        <FavoriteIcon sx={{ fontSize: 22 }} />
      ) : (
        <FavoriteBorderIcon sx={{ fontSize: 22 }} />
      )}
    </IconButton>
  );
}
